import { HIERARCHY_MAX_DEPTH, HIERARCHY_NODE_CAP, type AccountHierarchyNode } from '@shared/flow/account-hierarchy';
import { DEAL_STATUS_LABEL, effectiveStatuses, type AccountDealSummary, type DealRollup, type FilterOption, type DealFilters, type WonByType } from '@shared/flow/account-hierarchy-deal-model';
import { HIERARCHY_DEALS_CAP, type DealStatus } from '@shared/flow/account-hierarchy-deals';
import {
  arrAtRiskOf, healthToneOf, type HealthTone, type WhitespaceStatus,
} from '@shared/flow/account-hierarchy-whitespace';
import { fiscalPeriodName, type FiscalPreset } from '@shared/flow/fiscal-period';
import { money, num } from '../accountFieldFormat';
import type { HealthKey, HierarchyLensKey, RenewalPreset } from '@shared/flow/account-hierarchy-filters';

export const HIERARCHY_COPY = {
  loading: 'Loading hierarchy…',
  loadingGraph: 'Loading graph…',
  error: 'Failed to load hierarchy.',
  empty: 'This account has no parent or child accounts in Terra.',
  truncated: `Showing part of this hierarchy — it has more than ${HIERARCHY_NODE_CAP} accounts or ${HIERARCHY_MAX_DEPTH} levels.`,
  current: 'Current',
  parentMissing: '(parent not in Terra)',
  /** The 500-node cap cut the tree: absence of children cannot be asserted. */
  childrenIncomplete: 'Not all loaded',
  seeMoreInTab: 'See more in the Hierarchy tab',
} as const;

export const DASH = '—';
export const NO_AMOUNT = 'No amount';

export const SEPARATE_BUYING_CENTER_LABEL = 'Separate buying center';
/** Shown when an account is flagged but Salesforce has no summary sentence for it. */
export const SEPARATE_BUYING_CENTER_FALLBACK = 'This account has a separate buying center from its parent.';

/** The one sentence explaining why an account is flagged (summary, else the plain fallback). */
export function buyingCenterText(node: Pick<AccountHierarchyNode, 'buying_center_summary'>): string {
  const summary = node.buying_center_summary?.trim();
  return summary ? summary : SEPARATE_BUYING_CENTER_FALLBACK;
}

export interface HierarchyColumn {
  key: string;
  label: string;
  align?: 'right' | 'center';
  /** Explains the column in a header tooltip. */
  help?: string;
}

export const HIERARCHY_COLUMNS: HierarchyColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'type', label: 'Type' },
  { key: 'employees', label: 'Employees', align: 'right' },
  { key: 'arr', label: 'ARR', align: 'right' },
  { key: 'pipeline', label: 'Open Pipeline', align: 'right' },
  { key: 'region', label: 'Region' },
  { key: 'contacts', label: 'Contacts', align: 'right' },
  { key: 'buyingCenter', label: SEPARATE_BUYING_CENTER_LABEL, align: 'center' },
  { key: 'owner', label: 'Owner' },
];

export const DEAL_COLUMN_HELP = {
  dealAmount: "Sum of the Salesforce Amount on this account's own deals that match the filters (no subsidiaries). USD.",
  dealArr: "Sum of the Salesforce ARR on this account's own deals that match the filters (no subsidiaries). USD.",
} as const;

/** Appended after Owner while the deal overlay is on (per-account values only). */
export const DEAL_COLUMNS: HierarchyColumn[] = [
  { key: 'deals', label: 'Deals', align: 'right' },
  { key: 'dealAmount', label: 'Deal amount', align: 'right', help: DEAL_COLUMN_HELP.dealAmount },
  { key: 'dealArr', label: 'Deal ARR', align: 'right', help: DEAL_COLUMN_HELP.dealArr },
];

/** The account's own contact count (never a subsidiary roll-up). */
export function contactsLabel(node: AccountHierarchyNode): string {
  return String(node.contact_count);
}

export function rankLabel(rank: number | null): string {
  return rank == null ? DASH : `#${rank}`;
}

export const hierarchyMoney = (v: number | null): string => money(v) ?? DASH;
export const hierarchyNum = (v: number | null): string => num(v) ?? DASH;

export function accountsMoreLabel(hiddenCount: number): string {
  return `+${hiddenCount} account${hiddenCount === 1 ? '' : 's'}`;
}

// ---- Deal overlay ("Deal activity") ----------------------------------------------------------

export const DEAL_OVERLAY_COPY = {
  emptyMatch: 'No accounts in this hierarchy have deals that match these filters.',
  emptyMatchTruncated: 'No matching deals in the loaded data; this hierarchy has more deals than could be loaded.',
  truncated: 'Deal totals are partial — this hierarchy has more deal groups than Terra can load at once.',
  loadingDeals: 'Loading deals…',
  dealsError: 'Failed to load deals.',
  noDeals: 'No matching deals.',
  wonByType: 'Won by deal type',
  truncatedList: `Showing the ${HIERARCHY_DEALS_CAP} most recent deals; matching deals may be older.`,
  untitledDeal: 'Untitled deal',
  dealsColumn: 'Deals',
  dealAmountColumn: 'Deal amount',
  dealArrColumn: 'Deal ARR',
} as const;

export const DEAL_DATE_RANGE_LABELS: Record<FiscalPreset, string> = {
  all: 'All time',
  this_year: 'This year',
  last_year: 'Last year',
  this_quarter: 'This quarter',
  last_quarter: 'Last quarter',
};

/** Whole-dollar USD, compact: $950, $45K, $1.3M, $1.5B. */
export function dealMoney(v: number | null): string {
  if (v == null) return DASH;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${trimZero((v / 1e9).toFixed(1))}B`;
  if (abs >= 1e6 || Math.round(abs / 1e3) >= 1000) return `$${trimZero((v / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
}

function trimZero(s: string): string {
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

/** "$1.3M · ARR $400K": both dollar views, everywhere a deal total is shown. */
export function dealAmountLine(amount: number | null, arr: number | null): string {
  return `${dealMoney(amount)} · ARR ${dealMoney(arr)}`;
}

/** "3 matching accounts · $1.3M opp · ARR $400K · 7 deals": the Deals lens summary (the root roll-up). */
export function dealsSummaryLabel(accounts: number, total: Pick<DealRollup, 'amount' | 'arr' | 'count'> | null): string {
  const noun = accounts === 1 ? 'matching account' : 'matching accounts';
  if (!total) return `${accounts} ${noun}`;
  return `${accounts} ${noun} · ${dealMoney(total.amount)} opp · ARR ${dealMoney(total.arr)} · ${total.count} ${total.count === 1 ? 'deal' : 'deals'}`;
}

/** "4 accounts renewing · $1.2M ARR at risk": the Renewals lens summary. */
export function renewalsSummaryLabel(accounts: number, arrAtRisk: number): string {
  return `${accounts} ${accounts === 1 ? 'account' : 'accounts'} renewing · ${dealMoney(arrAtRisk)} ARR at risk`;
}

/** A deal amount, or "No amount" when Salesforce has none (null or 0): never "$0" or a bare dash. */
export function dealAmountText(amount: number | null): string {
  return amount == null || amount === 0 ? NO_AMOUNT : dealMoney(amount);
}

/** "Opp $75K · ARR $40K": the sum of an account's matching deals' amounts, labeled (or "No amount · ARR $40K"). */
export function oppAmountLine(amount: number | null, arr: number | null): string {
  const first = amount == null || amount === 0 ? NO_AMOUNT : `Opp ${dealMoney(amount)}`;
  return `${first} · ARR ${dealMoney(arr)}`;
}

/**
 * "+1 more open deal · $45K": matching deals beyond the primary one on the card. The status word
 * follows the active status filter (several statuses, or the all-but-disqualified default, say
 * "deals"); the dollars are what the account's matching deals sum to minus the primary deal.
 */
export function moreDealsLabel(
  summary: Pick<AccountDealSummary, 'primary' | 'moreCount' | 'amount'>,
  statuses: readonly DealStatus[],
): string | null {
  if (summary.moreCount <= 0) return null;
  const only = statuses.length === 1 ? statuses[0] : null;
  const noun = summary.moreCount === 1 ? 'deal' : 'deals';
  const word = only ? `${DEAL_STATUS_LABEL[only].toLowerCase()} ${noun}` : noun;
  const remaining = summary.amount - (summary.primary?.amount ?? 0);
  return `+${summary.moreCount} more ${word}${remaining > 0 ? ` · ${dealMoney(remaining)}` : ''}`;
}

/** The card's primary-deal line ("Name · $50K" + "+2 more open deals · $45K"); null when the account has no named deal. */
export function primaryDealLine(
  summary: Pick<AccountDealSummary, 'primary' | 'moreCount' | 'amount'>,
  filters: Pick<DealFilters, 'statuses'>,
): { text: string; more: string | null } | null {
  if (!summary.primary) return null;
  const { name, amount } = summary.primary;
  return {
    text: `${name ?? DEAL_OVERLAY_COPY.untitledDeal} · ${dealAmountText(amount)}`,
    more: moreDealsLabel(summary, effectiveStatuses(filters)),
  };
}

export function dealStatusSelectionLabel(statuses: readonly DealStatus[]): string {
  return statuses.length === 0 ? FILTER_BAR_COPY.allExceptDisqualified : statuses.map(st => DEAL_STATUS_LABEL[st]).join(', ');
}

export function selectionLabel(selection: DealFilters['pipelines'], options: readonly FilterOption[]): string {
  if (selection === 'all') return FILTER_BAR_COPY.allSelection;
  if (selection.length === 0) return FILTER_BAR_COPY.noneSelection;
  if (selection.length === 1) return options.find(o => o.key === selection[0])?.label ?? selection[0];
  return `${selection.length} selected`;
}

/** Health trigger text: "All" when nothing is picked, the names for one or two, else a count. */
export function healthSelectionLabel(keys: readonly HealthKey[]): string {
  if (keys.length === 0) return FILTER_BAR_COPY.allSelection;
  return keys.length <= 2 ? keys.map(k => HEALTH_LABEL[k]).join(', ') : `${keys.length} selected`;
}

export function dealRangeLabel(preset: FiscalPreset, now: Date): string {
  const period = fiscalPeriodName(preset, now);
  return period ? `${DEAL_DATE_RANGE_LABELS[preset]} (${period})` : DEAL_DATE_RANGE_LABELS[preset];
}

// ---- Color by (customer status) + renewals ------------------------------------------------------

export const LENS_LABEL: Record<HierarchyLensKey, string> = {
  deals: 'Deals', renewals: 'Renewals',
};

export const RENEWAL_PRESET_LABEL: Record<RenewalPreset, string> = {
  any: 'Any date',
  next_30: 'Next 30 days',
  next_60: 'Next 60 days',
  next_90: 'Next 90 days',
  past_due: 'Past due',
  this_fiscal_quarter: 'This fiscal quarter',
  next_fiscal_quarter: 'Next fiscal quarter',
  custom: 'Custom range',
};

export const HEALTH_LABEL: Record<HealthKey, string> = { green: 'Green', yellow: 'Yellow', red: 'Red', none: 'None' };

/** The deal status checkboxes read as in Salesforce ("Closed won"), not the short chip words. */
export const DEAL_STATUS_FIELD_LABEL: Record<DealStatus, string> = {
  open: 'Open', won: 'Closed won', lost: 'Closed lost', disqualified: 'Disqualified',
};

export const FILTER_BAR_COPY = {
  statusLabel: 'Status',
  dateRangeLabel: 'Date range',
  dateRangeHint: 'By close date',
  pipelineLabel: 'Pipeline',
  dealTypeLabel: 'Deal type',
  renewalDateLabel: 'Renewal date',
  customFrom: 'From',
  customTo: 'To',
  healthLabel: 'Health',
  sidebarTitle: 'Filters',
  customerStatusTitle: 'Customer status',
  resetAll: 'Reset all',
  filtersButton: 'Filters',
  showOnlyMatching: 'Show only matching',
  allSelection: 'All',
  noneSelection: 'None',
  allExceptDisqualified: 'All except disqualified',
  customRange: 'Custom range…',
  rangeInvalidDate: 'Enter a valid date.',
  rangeInverted: 'From must be on or before To.',
} as const;

/** The sidebar toggle in the tab header: "Filters", or "Filters (2)" with active filters. */
export function filtersButtonLabel(count: number): string {
  return count > 0 ? `${FILTER_BAR_COPY.filtersButton} (${count})` : FILTER_BAR_COPY.filtersButton;
}

export const LENS_COPY = {
  legendLabel: 'Customer status',
  openRenewalOpp: 'Open renewal opp in Salesforce',
  noRenewals: 'No accounts in this hierarchy renew in this date range.',
} as const;

export const WHITESPACE_LABEL: Record<WhitespaceStatus, string> = {
  customer: 'Customer',
  open_pipeline: 'Open pipeline',
  churned: 'Churned',
  prospect_contacts: 'Prospect with contacts',
  untouched: 'Untouched',
};

/** One-line meaning of each color, for the legend's tooltips. */
export const WHITESPACE_HELP: Record<WhitespaceStatus, string> = {
  customer: 'Active subscription ARR above $0.',
  open_pipeline: 'No ARR yet, but open pipeline.',
  churned: 'A former customer (Churned Customer / Churned Account) with no open pipeline.',
  prospect_contacts: 'No ARR or pipeline, but we have contacts here.',
  untouched: 'No ARR, pipeline or contacts: whitespace.',
};

/**
 * Semantic Glaze tokens only (never raw hex or the default Tailwind palette). `bg` / `border` are
 * the card's tone; `dot` is the legend / table swatch. NEVER carries opacity: dimming (a lens
 * that does not match) owns opacity, so the tone and the dim can never fight.
 */
export const WHITESPACE_TONE: Record<WhitespaceStatus, { bg: string; border: string; card: string; dot: string }> = {
  customer: { bg: 'bg-bg-green-light', border: 'border-border-green', card: 'bg-bg-green-light border-border-green', dot: 'bg-border-green' },
  open_pipeline: { bg: 'bg-bg-blue-light', border: 'border-border-blue', card: 'bg-bg-blue-light border-border-blue', dot: 'bg-border-blue' },
  churned: { bg: 'bg-bg-purple-light', border: 'border-border-purple', card: 'bg-bg-purple-light border-border-purple', dot: 'bg-border-purple' },
  prospect_contacts: { bg: 'bg-bg-yellow-light', border: 'border-border-yellow', card: 'bg-bg-yellow-light border-border-yellow', dot: 'bg-border-yellow' },
  untouched: { bg: 'bg-bg-secondary', border: 'border-border-secondary', card: 'bg-bg-secondary border-border-secondary', dot: 'bg-border-primary' },
};

export function renewalBadgeLabel(days: number): string {
  return days === 0 ? 'Renews today' : `Renews in ${days}d`;
}

/** Glaze Badge color for a health tone (the badge doubles as the health cue). */
export const HEALTH_BADGE_COLOR: Record<HealthTone, 'green' | 'yellow' | 'red'> = { success: 'green', warning: 'yellow', danger: 'red' };

/** "a, b, c +1 more": reasons on one line, capped at three. */
export function reasonsLabel(reasons: readonly string[]): string {
  const shown = reasons.slice(0, 3).join(', ');
  return reasons.length > 3 ? `${shown} +${reasons.length - 3} more` : shown;
}

export interface RenewalTooltipFacts {
  date: string;
  arrAtRisk: string;
  health: { label: string; tone: HealthTone | null } | null;
  reasons: string[];
}

type RenewalNode = Pick<
  AccountHierarchyNode,
  'enterprise_renewal_date' | 'contracted_arr' | 'arr_active_subscriptions' | 'health_rating' | 'health_red_reason' | 'renewal_risk_reasons'
>;

/** Everything the renewal badge's tooltip shows, formatted; the caller guarantees a renewal date. */
export function renewalTooltipFacts(node: RenewalNode): RenewalTooltipFacts {
  const rating = node.health_rating?.trim() || null;
  return {
    date: node.enterprise_renewal_date ?? DASH,
    arrAtRisk: dealMoney(arrAtRiskOf(node)),
    health: rating ? { label: rating, tone: healthToneOf(rating) } : null,
    reasons: [...new Set([...node.health_red_reason, ...node.renewal_risk_reasons])],
  };
}

/** Table "Status" column header (shown only while Color by is on). */
export const STATUS_COLUMN: HierarchyColumn = { key: 'status', label: 'Status' };

// ---- Tooltips, a11y and layout polish ---------------------------------------------------------

/** "Open all 5 deals" (the Opps tab lists every deal); a plus when the tooltip's list was capped. */
export function openAllDealsLabel(total: number, capped: boolean): string {
  return total === 1 ? 'Open deal' : `Open all ${total}${capped ? '+' : ''} deals`;
}

/** "Flat Renewal: 2 · $900K · ARR $900K": one line of the won-by-deal-type split. */
export function wonSplitLabel(w: WonByType): string {
  return `${w.dealType ?? 'No type'}: ${w.count} · ${oppAmountLine(w.amount, w.arr)}`;
}

export function edgeTooltipText(targetName: string, count: number, amount: number, arr: number): string {
  return `${targetName} and every account beneath it: ${count} ${count === 1 ? 'deal' : 'deals'} · Opp amount ${dealMoney(amount)} · ARR ${dealMoney(arr)}`;
}

/** Screen-reader status for the lenses ("3 of 12 accounts match the active filters"). */
export function lensMatchStatus(matching: number, total: number): string {
  return `${matching} of ${total} ${total === 1 ? 'account matches' : 'accounts match'} the active filters`;
}

/** "3 of 12 accounts": how many accounts the active lenses highlight (all of them when none is on). */
export function rowCountLabel(matching: number, total: number): string {
  return `${matching} of ${total} ${total === 1 ? 'account' : 'accounts'}`;
}
